import articleView from "app/analytics/mutations/articleView"
import { SlugAndPage } from "app/articles/codecs/SlugAndPage"
import { ArticleDetails, ArticleDetailsProps } from "app/articles/components/ArticleDetails"
import { NortherPageProps } from "app/common/components/NortherPageProps"
import { decodeWithTE } from "app/common/core/decode"
import { InvalidPageNumberError } from "app/common/errors/InvalidPageNumberError"
import { justThrow } from "app/common/fp-ts"
import { logException } from "app/common/logging"
import { bindCommon } from "app/common/next"
import { UrlMgr } from "app/common/routes"
import { Localified } from "app/common/seo/alternatePages"
import { ldJsonArticle } from "app/common/seo/jdJson"
import { OgType } from "app/common/seo/openGraph"
import { createSeoMetadata } from "app/common/seo/seoEssentials"
import {
  getStaticPathsResult,
  getStaticPropsResult,
  notFoundOrThrow,
  StaticPath,
} from "app/common/ui/next"
import { range1 } from "app/common/utilities/array"
import { parseEntityIds } from "app/common/utilities/structuredText"
import { localeToCanadianLocale } from "app/common/utilities/types"
import { toTitleString } from "app/common/web"
import {
  getArticleBySlug,
  getArticlePaths,
  getPopularArticles,
  getRecentArticles,
} from "app/data/articles/article.queries"
import { getBrandsByIds } from "app/data/brands/brand.queries"
import BlogLayout from "app/layouts/BlogLayout"
import { ArticleVM, BrandVM } from "app/models"
import { decodeToArticleMap, getPageVM } from "app/models/article.map"
import { BlitzPage, GetStaticPaths, GetStaticProps, useMutation } from "blitz"
import { LocaleD } from "db"
import { apply, array, option, readonlyNonEmptyArray, task, taskEither } from "fp-ts"
import { decrement, flow, increment, pipe } from "fp-ts/function"
import React, { useEffect } from "react"

interface ArticlePagePathData {
  number: number
  slug: Localified<string>
  updatedAt: Date
}

interface ArticlePagePath {
  locale: LocaleD
  number: number
  slug: string
}

export const getArticlePagePathData = (): taskEither.TaskEither<
  Error,
  Array<ArticlePagePathData>
> =>
  pipe(
    getArticlePaths(),
    taskEither.map(
      array.chain(({ _count, translations, updatedAt }) =>
        pipe(
          range1(_count.pages),
          array.map(
            (number): ArticlePagePathData => ({
              number,
              slug: {
                en_ca: pipe(translations, readonlyNonEmptyArray.head).slug,
                fr_ca: pipe(translations, readonlyNonEmptyArray.last).slug,
              },
              updatedAt,
            })
          )
        )
      )
    )
  )

const mapArticlePagePathDataToArticlePagePath = ({
  number,
  slug: { en_ca, fr_ca },
}: ArticlePagePathData): [ArticlePagePath, ArticlePagePath] => [
  {
    locale: LocaleD.en_ca,
    number,
    slug: en_ca,
  },
  {
    locale: LocaleD.fr_ca,
    number,
    slug: fr_ca,
  },
]

type ArticlePath = StaticPath<{
  slugAndPage: [string] | [string, string]
}>

const mapArticlePagePathToArticlePath = ({
  locale,
  number,
  slug,
}: ArticlePagePath): ArticlePath => ({
  locale: localeToCanadianLocale(locale),
  params: {
    slugAndPage: number === 1 ? [slug] : [slug, number.toString()],
  },
})

// ts-prune-ignore-next
export const getStaticPaths: GetStaticPaths = () =>
  pipe(
    getArticlePagePathData(),
    taskEither.map(array.map(mapArticlePagePathDataToArticlePagePath)),
    taskEither.map(array.flatten),
    taskEither.map(array.map(mapArticlePagePathToArticlePath)),
    taskEither.bimap(logException, getStaticPathsResult),
    taskEither.fold(justThrow, task.of)
  )()

type ArticlePageProps = NortherPageProps<{
  articleDetails: ArticleDetailsProps
  defaultLocale: LocaleD
  locale: LocaleD
}>

const articleMapBySlug = flow(getArticleBySlug, taskEither.chainW(decodeToArticleMap))

const getSlugAndPage = decodeWithTE(SlugAndPage)

const articleVmMap = (article: ArticleVM, locale: LocaleD): ldJsonArticle => ({
  "@context": "https://schema.org",
  "@type": "Article",
  author: {
    "@type": "Person",
    name: article.authors[0]?.name ?? "",
  },
  dateModified: article.updatedAt.toISOString(),
  datePublished: article.publishedAt.toISOString(),
  headline: article.title,
  name: article.title,
  publisher: {
    "@type": "Organization",
    name: "Norther.ca",
  },
  url: UrlMgr.ArticlePage({
    locale,
    slug: article.slug,
  }),
})

// ts-prune-ignore-next
export const getStaticProps: GetStaticProps<ArticlePageProps> = ({ locale, params }) =>
  pipe(
    taskEither.Do,
    bindCommon(locale),
    taskEither.apS("slugAndPage", getSlugAndPage(params?.slugAndPage)),
    taskEither.bind("slug", ({ slugAndPage: [slug] }) => taskEither.of(slug)),
    taskEither.bind("number", ({ slugAndPage: [, page] }) => taskEither.of(page)),
    taskEither.bind("articleMap", ({ slug }) => articleMapBySlug(slug)),
    taskEither.bind("article", ({ articleMap, locale }) => pipe(articleMap[locale], taskEither.of)),
    taskEither.bindW("page", ({ article, number }) =>
      pipe(
        getPageVM(article, number),
        taskEither.fromOption(() => InvalidPageNumberError.of(number))
      )
    ),
    taskEither.bind("other", ({ article: { id: articleId }, locale }) =>
      apply.sequenceS(taskEither.ApplicativePar)({
        popularArticles: getPopularArticles({ articleId, locale }),
        recentArticles: getRecentArticles({ articleId, locale, take: 5 }),
        trendingArticles: getPopularArticles({
          articleId,
          inPastNDays: 7,
          locale,
        }),
      })
    ),
    taskEither.bind("st", ({ page }) =>
      pipe(page, (a) => a.content, parseEntityIds, taskEither.of)
    ),
    taskEither.bind("contentBrands", ({ locale, st: { brandIds } }) =>
      pipe(
        brandIds,
        option.fromPredicate(array.isNonEmpty),
        option.map(getBrandsByIds(locale)),
        option.getOrElse(() => taskEither.of([] as readonly BrandVM[]))
      )
    ),
    taskEither.bind("entities", ({ contentBrands }) => taskEither.of({ brands: contentBrands })),
    taskEither.bind(
      "articleDetails",
      ({
        article,
        entities,
        locale,
        number,
        other: { popularArticles, recentArticles, trendingArticles },
        page,
      }) =>
        taskEither.of({
          article,
          entities,
          locale,
          paging: {
            currentPage: page,
            nextPage: getPageVM(article, increment(number)),
            prevPage: getPageVM(article, decrement(number)),
          },
          popularArticles,
          recentArticles,
          trendingArticles,
        })
    ),
    taskEither.bind("seoMetadata", ({ article, articleMap, locale, ogLocale, page, t }) =>
      pipe(
        {
          canonicalUrl: UrlMgr.ArticlePage({
            locale,
            number: page.number,
            slug: article.slug,
          }),
          ldJsonArticle: articleVmMap(article, locale),
          metaDescription: page.summary,
          openGraphMetadata: {
            description: page.summary,
            locale: ogLocale,
            siteName: "Norther",
            title: toTitleString([article.title, t("buy-local-buy-canadian"), `Norther`]),
            type: option.some(OgType.Article),
            url: UrlMgr.ArticlePage({
              locale,
              number: page.number,
              slug: articleMap.en_ca.slug,
            }),
          },
          title: toTitleString([
            ...(page.number > 1 ? [page.title] : []),
            article.title,
            t("buy-local-buy-canadian"),
            `Norther`,
          ]),
          urlMap: {
            en_ca: UrlMgr.ArticlePage({
              number: page.number,
              slug: articleMap.en_ca.slug,
            }),
            fr_ca: UrlMgr.ArticlePage({
              locale: LocaleD.fr_ca,
              number: page.number,
              slug: articleMap.fr_ca.slug,
            }),
            "x-default": UrlMgr.ArticlePage({
              number: page.number,
              slug: articleMap.en_ca.slug,
            }),
          },
        },
        createSeoMetadata,
        taskEither.of
      )
    ),
    taskEither.map(({ articleDetails, locale, seoMetadata }) => ({
      articleDetails,
      locale,
      seoMetadata,
    })),
    taskEither.bimap(logException, getStaticPropsResult),
    taskEither.fold(notFoundOrThrow, task.of)
  )()

const ArticlePage: BlitzPage<ArticlePageProps> = ({ articleDetails, locale }) => {
  const [createArticleView] = useMutation(articleView)

  const pageId = articleDetails.paging.currentPage.id

  useEffect(() => {
    createArticleView({
      locale,
      pageId,
    })
  }, [createArticleView, locale, pageId])

  return <ArticleDetails {...articleDetails} />
}

ArticlePage.suppressFirstRenderFlicker = true

ArticlePage.getLayout = BlogLayout.getLayout

// ts-prune-ignore-next
export default ArticlePage
